import React, { Fragment, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, CheckCircle2, ChevronDown, CreditCard, Info } from "lucide-react";
import PageHeader from "../../components/common/PageHeader";
import { formatMessageRate } from "../../utils/currencyFormatters";

const plans = [
  {
    name: "Basic",
    nameColor: "text-slate-900",
    priceLabel: "\u20B9999",
    monthlyPrice: 999,
    description: "Entry plan for small teams and starter campaigns.",
    subPrice: "/mo (+taxes)",
    buttonText: "Try 14-Day Trial",
    buttonClass: "bg-slate-300 hover:bg-slate-400 text-slate-900",
    chooseIntro:
      "Get automated quick-flows for price please queries & giveaway campaigns",
    chooseItems: ["Live chat Inbox", "Owner Roles Only", "Campaign"],
    limits: [
      "Unlimited Messages (Based on your WhatsApp Number)",
      "Unlimited Contacts",
      "15 Custom Fields",
      "15 Custom Tags",
    ],
    chooseBg: "bg-white",
    limitsBg: "bg-white",
    bulletColor: "text-slate-500",
    headerBg: "bg-white",
    columnBg: "bg-white",
    backendPlanKey: "Basic",
  },
  {
    name: "Growth",
    nameColor: "text-slate-900",
    priceLabel: "\u20B92,799",
    monthlyPrice: 2799,
    description: "Best fit for scaling automation, campaigns and catalog flows.",
    subPrice: "/mo (+taxes)",
    buttonText: "Try 14-Day Trial",
    buttonClass: "bg-[#2E8B57] hover:bg-[#20613d] text-white",
    chooseIntro: "Everything in Basic, Plus",
    chooseItems: [
      "FAQ automations & linear chatbot flows",
      "Advanced campaigns",
      "Catalogs",
      "Native payments",
      "Public APIs",
      "Agents Roles",
    ],
    limits: [
      "Unlimited Messages (Based on your WhatsApp Number)",
      "Unlimited Contacts",
      "25 Custom Fields",
      "30 Custom Tags",
      "5 Custom Events",
      "4 Team Members",
    ],
    chooseBg: "bg-[#eaf3ea]",
    limitsBg: "bg-[#eaf3ea]",
    bulletColor: "text-emerald-600",
    headerBg: "bg-[#eaf3ea]",
    columnBg: "bg-[#eaf3ea]",
    backendPlanKey: "Growth",
  },
  {
    name: "Pro",
    nameColor: "text-slate-900",
    priceLabel: "\u20B93,799",
    monthlyPrice: 3799,
    description: "Advanced flows, assignment, webhooks, and higher scale.",
    subPrice: "/mo (+taxes)",
    buttonText: "Try 14-Day Trial",
    buttonClass: "bg-sky-500 hover:bg-sky-600 text-white",
    chooseIntro: "Everything in Growth, Plus",
    chooseItems: [
      "Get advanced chatbot flows with branching, API calls & Conditions",
      "Chat Auto-assignment",
      "Advanced webhooks",
    ],
    limits: [
      "Unlimited Messages (Based on your WhatsApp Number)",
      "Unlimited Contacts",
      "30 Custom Fields",
      "45 Custom Tags",
      "7 Custom Events",
      "10 Team Members",
    ],
    chooseBg: "bg-white",
    limitsBg: "bg-white",
    bulletColor: "text-sky-500",
    headerBg: "bg-white",
    columnBg: "bg-white",
    backendPlanKey: "Pro",
  },
  {
    name: "Enterprise",
    nameColor: "text-slate-900",
    priceLabel: "On request",
    description: "Custom solution for high-volume and enterprise support needs.",
    buttonText: "Get In Touch",
    buttonClass: "bg-slate-800 hover:bg-slate-900 text-white",
    chooseIntro: "Everything in Pro, Plus",
    chooseItems: [
      "Higher Rate Limits",
      "Better Campaign Speeds",
      "No Templates Markups",
      "Personalised Support",
    ],
    limits: [
      "Unlimited Messages (Based on your WhatsApp Number)",
      "Unlimited Contacts",
      "Unlimited Custom Fields",
      "Unlimited Custom Tags",
      "Unlimited Custom Events",
      "Unlimited Team Members",
    ],
    chooseBg: "bg-white",
    limitsBg: "bg-white",
    bulletColor: "text-slate-500",
    headerBg: "bg-white",
    columnBg: "bg-white",
    backendPlanKey: null,
  },
];

const marketRows = [
  { feature: "Campaign Scheduler", basic: { type: "none" }, growth: { type: "check" }, pro: { type: "check" }, enterprise: { type: "check" } },
  { feature: "Campaign Auto-retries", basic: { type: "none" }, growth: { type: "check" }, pro: { type: "check" }, enterprise: { type: "check" } },
  { feature: "Click Tracking (Templates sent in Campaigns)", basic: { type: "none" }, growth: { type: "check" }, pro: { type: "check" }, enterprise: { type: "check" } },
  { feature: "Conversion Tracking via Custom Events (Campaigns)", basic: { type: "none" }, growth: { type: "check" }, pro: { type: "check" }, enterprise: { type: "check" } },
  {
    feature: "Post-reply-flows for Campaigns",
    basic: { type: "none" },
    growth: { type: "text", value: "Send Products, Send Interactive List, Send Custom Reply, Send Workflow" },
    pro: { type: "text", value: "Send Products, Send Interactive List, Send Custom Reply, Send Workflow" },
    enterprise: { type: "text", value: "Send Products, Send Interactive List, Send Custom Reply, Send Workflow" },
  },
  {
    feature: "Template Types",
    basic: { type: "none" },
    growth: { type: "text", value: "Send Products Catalog, Carousel, WhatsApp Form, WhatsApp Pay" },
    pro: { type: "text", value: "Send Products Catalog, Carousel, WhatsApp Form, WhatsApp Pay" },
    enterprise: { type: "text", value: "Send Products Catalog, Carousel, WhatsApp Form, WhatsApp Pay" },
  },
  { feature: "CTWA Ads - Launcher", basic: { type: "none" }, growth: { type: "check" }, pro: { type: "check" }, enterprise: { type: "check" } },
  { feature: "WhatsApp Forms Creation", basic: { type: "none" }, growth: { type: "check" }, pro: { type: "check" }, enterprise: { type: "check" } },
  { feature: "WhatsApp Forms Sending", basic: { type: "none" }, growth: { type: "none" }, pro: { type: "none" }, enterprise: { type: "none" } },
];

const supportRows = [
  { feature: "Shared Team Inbox", basic: { type: "none" }, growth: { type: "check" }, pro: { type: "check" }, enterprise: { type: "check" } },
  {
    feature: "Chats Auto-assignment: Load Equalizer",
    basic: { type: "none" },
    growth: { type: "text", value: "Load Equalizing Assignment Within Chatbot" },
    pro: { type: "text", value: "Load Equalizing Assignment Within Chatbot Assignment based on Custom Fields" },
    enterprise: { type: "text", value: "Load Equalizing Assignment Within Chatbot Assignment based on Custom Fields" },
  },
  { feature: "Conversation Analytics", basic: { type: "none" }, growth: { type: "check" }, pro: { type: "check" }, enterprise: { type: "check" } },
  { feature: "Agent Performance Analytics", basic: { type: "none" }, growth: { type: "check" }, pro: { type: "check" }, enterprise: { type: "check" } },
  {
    feature: "No-Code Chatbot Builder",
    basic: { type: "none" },
    growth: { type: "text", value: "Basic [Linear Chatbot]" },
    pro: { type: "text", value: "Basic [Linear Chatbot] + Pro [Branching, Logical Flows, API calls, auto-assign agent]" },
    enterprise: { type: "text", value: "Basic [Linear Chatbot] + Pro [Branching, Logical Flows, API calls, auto-assign agent]" },
  },
  { feature: "WhatsApp Forms - Creation", basic: { type: "none" }, growth: { type: "check" }, pro: { type: "check" }, enterprise: { type: "check" } },
];

const commerceRows = [
  { feature: "Create WhatsApp Catalog", basic: { type: "none" }, growth: { type: "check" }, pro: { type: "check" }, enterprise: { type: "check" } },
  { feature: "WhatsApp Checkout Bot", basic: { type: "none" }, growth: { type: "check" }, pro: { type: "check" }, enterprise: { type: "check" } },
  { feature: "Order Management Panel", basic: { type: "none" }, growth: { type: "check" }, pro: { type: "check" }, enterprise: { type: "check" } },
  { feature: "Native WhatsApp Payment (WhatsApp Pay)", basic: { type: "none" }, growth: { type: "check" }, pro: { type: "check" }, enterprise: { type: "check" } },
  { feature: "Bulk Upload Contacts", basic: { type: "none" }, growth: { type: "check" }, pro: { type: "check" }, enterprise: { type: "check" } },
  { feature: "Send Payment Links from Inbox", basic: { type: "none" }, growth: { type: "check" }, pro: { type: "check" }, enterprise: { type: "check" } },
];

const apiRows = [
  {
    feature: "Public APIs",
    basic: { type: "none" },
    growth: { type: "text", value: "Message send Api, Template Send API, Non-Template Send API, Get Users API, Create Template API, Create API Campaign" },
    pro: { type: "text", value: "Message send Api, Template Send API, Non-Template Send API, Get Users API, Create Template API, Create API Campaign" },
    enterprise: { type: "text", value: "Message send Api, Template Send API, Non-Template Send API, Get Users API, Create Template API, Create API Campaign" },
  },
  {
    feature: "Public Webhooks",
    basic: { type: "none" },
    growth: { type: "text", value: "Account Alerts, Template Alerts, Template Delivery Status, Carts & Orders, Payment Confirmations" },
    pro: { type: "text", value: "Account Alerts, Template Alerts, Template Delivery Status, Carts & Orders, Payment Confirmations, Incoming Messages, Chatbot Messages" },
    enterprise: { type: "text", value: "Account Alerts, Template Alerts, Template Delivery Status, Carts & Orders, Payment Confirmations, Incoming Messages, Chatbot Messages" },
  },
];

const teamRows = [
  { feature: "Create Team", basic: { type: "none" }, growth: { type: "text", value: "4 team member" }, pro: { type: "text", value: "10 team members" }, enterprise: { type: "text", value: "Unlimited" } },
  { feature: "Assign Role", basic: { type: "none" }, growth: { type: "check" }, pro: { type: "check" }, enterprise: { type: "check" } },
];

const matrixSections = [
  { id: "market", title: "Campaign", rows: marketRows },
  { id: "support", title: "Reporting and Analytics", rows: supportRows },
  { id: "commerce", title: "Selling on WhatsApp", rows: commerceRows },
  { id: "team", title: "Team Management", rows: teamRows },
  { id: "apis", title: "Developer & integration layer", rows: apiRows },
];

const templateMessageRates = {
  marketing: formatMessageRate(0.8631, "INR"),
  utility: formatMessageRate(0.115, "INR"),
  authentication: formatMessageRate(0.115, "INR"),
  service: formatMessageRate(0, "INR"),
};

function MatrixTickIcon() {
  return (
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 text-emerald-600">
      <Check className="h-[18px] w-[18px] stroke-[2.75]" />
    </span>
  );
}

export default function BillingPage() {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [openSections, setOpenSections] = useState({
    market: true,
    support: true,
    commerce: true,
    team: true,
    apis: true,
  });

  const getCyclePrice = basePrice => {
    if (!basePrice) return null;
    if (billingCycle === "quarterly") return Math.round(basePrice * 0.92);
    if (billingCycle === "yearly") return Math.round(basePrice * 0.8);
    return basePrice;
  };

  const toggleSection = id => {
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toCheckout = plan => {
    if (plan.name === "Enterprise" || !plan.backendPlanKey) {
      window.location.href = "mailto:sales@xplorebyte.com";
      return;
    }

    const checkoutCycle =
      billingCycle === "quarterly"
        ? "Quarterly"
        : billingCycle === "yearly"
          ? "Yearly"
          : "Monthly";
    navigate("/app/billing/checkout", {
      state: {
        planKey: plan.name,
        backendPlanKey: plan.backendPlanKey,
        billingCycle: checkoutCycle,
      },
    });
  };

  const pricingFontStyle = useMemo(
    () => ({ fontFamily: "Manrope, ui-sans-serif, system-ui, sans-serif" }),
    [],
  );

  return (
    <div className="min-h-screen bg-[#f3f4f6] py-6">
      <section className="px-4" style={pricingFontStyle}>
        <div className="mx-auto mb-8 max-w-[1240px] px-4 pt-4 pb-2">
          <PageHeader
            icon={CreditCard}
            title="Billing"
            subtitle="Review plans, billing cycles, and subscription details for your workspace."
          />
        </div>

        <div className="mx-auto mb-5 flex max-w-[1240px] justify-center">
          <div className="inline-flex items-center rounded-full bg-slate-200 p-1">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`rounded-full px-7 py-2 text-sm font-semibold transition-colors ${
                billingCycle === "monthly"
                  ? "bg-emerald-500 text-white"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("quarterly")}
              className={`rounded-full px-6 py-2 text-sm font-semibold transition-colors ${
                billingCycle === "quarterly"
                  ? "bg-emerald-500 text-white"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Quarterly <span className="text-xs font-bold text-amber-500">▼8%</span>
            </button>
            <button
              onClick={() => setBillingCycle("yearly")}
              className={`rounded-full px-6 py-2 text-sm font-semibold transition-colors ${
                billingCycle === "yearly"
                  ? "bg-emerald-500 text-white"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Yearly <span className="text-xs font-bold text-amber-500">▼20%</span>
            </button>
          </div>
        </div>

        <div className="mx-auto max-w-[1240px] overflow-x-auto overflow-y-visible pt-4 pb-12 lg:overflow-visible">
          <div className="min-w-[1240px]">
            <div className="sticky top-0 z-30 overflow-hidden rounded-t-[8px] border border-slate-300 bg-white">
              <div className="grid grid-cols-[230px_repeat(4,minmax(0,1fr))]">
                <div className="p-4">
                  <h1 className="text-[16px] font-semibold leading-tight text-slate-700">
                    Start with the plan that fits your stage
                  </h1>
                </div>

                {plans.map(plan => (
                  <div
                    key={`sticky-${plan.name}`}
                    className={`relative border-l border-slate-300 p-3.5 ${plan.headerBg}`}
                  >
                    {plan.name === "Growth" && (
                      <div className="absolute top-1.5 right-1.5 flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                        <Check className="h-2 w-2" />
                        <span>Most Popular</span>
                      </div>
                    )}
                    <p className={`text-[22px] font-bold tracking-tight ${plan.nameColor}`}>
                      {plan.name}
                    </p>

                    <div className="mt-2 flex flex-wrap items-end gap-1 text-slate-900">
                      <span className="text-[26px] font-semibold leading-none">
                        {getCyclePrice(plan.monthlyPrice)
                          ? `\u20B9${getCyclePrice(plan.monthlyPrice).toLocaleString("en-IN")}`
                          : plan.priceLabel}
                      </span>
                      {plan.subPrice && (
                        <span className="pb-1 text-sm font-semibold text-slate-600">
                          {plan.subPrice}
                        </span>
                      )}
                    </div>

                    <button
                      onClick={() => toCheckout(plan)}
                      className={`mt-3 w-full rounded-lg px-3 py-2 text-sm font-semibold tracking-wide transition-colors ${plan.buttonClass}`}
                    >
                      {plan.buttonText}
                    </button>
                    {plan.name !== "Enterprise" && (
                      <p className="mt-2 inline-flex w-full items-center justify-center gap-1 text-[11px] font-semibold text-slate-500">
                        <Info className="h-3 w-3" />
                        No credit card required
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="overflow-hidden rounded-b-[8px] border-x border-b border-slate-300 bg-white">
              <div className="grid grid-cols-[230px_repeat(4,minmax(0,1fr))]">
                <div className="border-b border-slate-300 p-4">
                  <p className="text-[17px] font-semibold leading-tight text-slate-800">
                    Per Template Message Charges
                  </p>
                </div>

                <div className="border-b border-l border-slate-300 bg-white p-2.5">
                  <p className="text-[15px] font-semibold leading-snug text-slate-900">Per Template Message Charges</p>
                  <div className="mt-2 space-y-1 text-sm">
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">Marketing</span><span className="font-semibold text-slate-900">{templateMessageRates.marketing}</span></div>
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">Utility</span><span className="font-semibold text-slate-900">{templateMessageRates.utility}</span></div>
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">Authentication</span><span className="font-semibold text-slate-900">{templateMessageRates.authentication}</span></div>
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">Service</span><span className="font-semibold text-slate-900">{templateMessageRates.service}</span></div>
                  </div>
                </div>

                <div className="border-b border-l border-slate-300 bg-[#eaf3ea] p-2.5">
                  <p className="text-[15px] font-semibold leading-snug text-slate-900">Per Template Message Charges</p>
                  <div className="mt-2 space-y-1 text-sm">
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">Marketing</span><span className="font-semibold text-slate-900">{templateMessageRates.marketing}</span></div>
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">Utility</span><span className="font-semibold text-slate-900">{templateMessageRates.utility}</span></div>
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">Authentication</span><span className="font-semibold text-slate-900">{templateMessageRates.authentication}</span></div>
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">Service</span><span className="font-semibold text-slate-900">{templateMessageRates.service}</span></div>
                  </div>
                </div>

                <div className="border-b border-l border-slate-300 bg-white p-2.5">
                  <p className="text-[15px] font-semibold leading-snug text-slate-900">Per Template Message Charges</p>
                  <div className="mt-2 space-y-1 text-sm">
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">Marketing</span><span className="font-semibold text-slate-900">{templateMessageRates.marketing}</span></div>
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">Utility</span><span className="font-semibold text-slate-900">{templateMessageRates.utility}</span></div>
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">Authentication</span><span className="font-semibold text-slate-900">{templateMessageRates.authentication}</span></div>
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">Service</span><span className="font-semibold text-slate-900">{templateMessageRates.service}</span></div>
                  </div>
                </div>

                <div className="border-b border-l border-slate-300 bg-white p-3">
                  <p className="text-[16px] font-semibold text-slate-900">Per Template Message Charges</p>
                  <div className="mt-3 space-y-1.5 text-sm">
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">Marketing</span><span className="font-semibold text-slate-900">Custom</span></div>
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">Utility</span><span className="font-semibold text-slate-900">{templateMessageRates.utility}</span></div>
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">Authentication</span><span className="font-semibold text-slate-900">{templateMessageRates.authentication}</span></div>
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">Service</span><span className="font-semibold text-slate-900">{templateMessageRates.service}</span></div>
                  </div>
                </div>

                <div className="p-4 text-[15px] font-semibold text-slate-700">What you get with this plan</div>

                {plans.map(plan => (
                  <div key={`${plan.name}-choose`} className={`border-b border-l border-slate-300 p-3 ${plan.chooseBg}`}>
                    <p className="text-base font-semibold text-slate-900">{plan.chooseIntro}</p>
                    <div className="mt-3 space-y-2.5">
                      {plan.chooseItems.map(item => (
                        <div key={`${plan.name}-${item}`} className="flex items-start gap-2.5">
                          <CheckCircle2 className={`mt-0.5 h-[18px] w-[18px] shrink-0 ${plan.bulletColor}`} />
                          <p className="text-sm leading-snug text-slate-900">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="p-4 text-[15px] font-semibold text-slate-700">Usage & capacity</div>

                {plans.map(plan => (
                  <div key={`${plan.name}-limits`} className={`border-l border-slate-300 p-3 ${plan.limitsBg}`}>
                    <div className="space-y-2.5">
                      {plan.limits.map(item => (
                        <div key={`${plan.name}-${item}`} className="flex items-start gap-2.5">
                          <CheckCircle2 className={`mt-0.5 h-[18px] w-[18px] shrink-0 ${plan.bulletColor}`} />
                          <p className="text-sm leading-snug text-slate-900">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {matrixSections.map(section => (
                <div key={section.id} className="border-t border-slate-300 bg-white">
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="flex w-full items-center justify-between bg-slate-100 px-4 py-2.5 text-left"
                    aria-expanded={openSections[section.id]}
                    aria-controls={`${section.id}-feature-table`}
                  >
                    <span className="text-lg font-semibold text-slate-900">{section.title}</span>
                    <ChevronDown
                      className={`h-5 w-5 text-slate-700 transition-transform ${
                        openSections[section.id] ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {openSections[section.id] && (
                    <div id={`${section.id}-feature-table`} className="grid grid-cols-[230px_repeat(4,minmax(0,1fr))]">
                      {section.rows.map(row => {
                        const cells = [row.basic, row.growth, row.pro, row.enterprise];
                        return (
                          <Fragment key={`${section.id}-${row.feature}`}>
                            <div className="flex min-h-[44px] items-center border-b border-slate-200 px-4 py-1.5 text-[14px] text-slate-900">
                              {row.feature}
                            </div>
                            {cells.map((cell, idx) => (
                              <div
                                key={`${section.id}-${row.feature}-${idx}`}
                                className={`flex min-h-[44px] items-center justify-center border-b border-l border-slate-200 px-4 py-1.5 text-center ${plans[idx].columnBg}`}
                              >
                                {cell.type === "check" && <MatrixTickIcon />}
                                {cell.type === "text" && (
                                  <p className="text-[14px] leading-snug text-slate-900">{cell.value}</p>
                                )}
                              </div>
                            ))}
                          </Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
